import { type ReactElement, useMemo } from "react";
import type {
  ContinueBrowsingBanner as ContinueBrowsingBannerModel,
  CwyloCarousel as CwyloCarouselModel,
} from "src/http/types";
import { PE_KEYS } from "src/shared/constants/PageEventKeys";
import { PE_TYPE } from "src/shared/constants/PageEventTypes";
import type { AppContext } from "src/shared/context/AppContext";
import { useAppContext } from "src/shared/context/AppContext";
import { useStorePageEvent } from "src/shared/hooks/useStorePageEvent";
import { storePageEvent as storePageEventFn } from "src/shared/utils/pageevents";
import type { CarouselSize } from "src/website-apps/shared/components/CarouselSize";
import { FanProtectMessage } from "src/website-apps/shared/components/fan-protect-banner/FanProtectMessage";
import { HOME_CAROUSEL_IDS } from "src/website-apps/shared/components/responsive-carousel/constants";
import { SEARCH_DISCOVERY_ENTITY_TYPES } from "src/website-apps/shared/utils/searchPageEvents";
import { ContinueBrowsingBanner } from "./continue-browsing-banner/ContinueBrowsingBanner";
import { CwyloCarouselOnHome } from "./cwylo-carousel/CwyloCarouselOnHome";

export interface CarouselToRender {
  carouselId: HOME_CAROUSEL_IDS;
  kind: number;
  shouldRender: boolean;
  render: () => ReactElement;
}

export interface UseCarouselSortingParams {
  carousels: CarouselToRender[];
  sortOrder: readonly number[];
  isMobile: boolean;
  moveUpRecentlyViewedCarousel: boolean;
  // Additional context needed for dynamic carousel insertion
  selectedCarouselGenre?: number;
  isStubhub?: boolean;
  hasSpotifyData?: boolean;
  spotifyConnectUrl?: string;
  isSpotifyConnected?: boolean;
  spotifyOptions?: unknown;
  carouselSize?: (typeof CarouselSize)[keyof typeof CarouselSize] | null;
  showRecentlyViewedAboveHeroBanner: boolean;
  showContinueBrowsingBanner?: boolean;
  continueBrowsingBanner?: ContinueBrowsingBannerModel | null;
  showContinueWhereYouLeftOff?: boolean;
  continueWhereYouLeftOffCarousel?: CwyloCarouselModel | null;
}

/**
 * Helper function to reorder carousels according to sort order and log page event.
 */
const reorder = (
  array: readonly CarouselToRender[],
  order: number[],
  appContext: AppContext,
  env: AppContext["env"],
) => {
  storePageEventFn(PE_TYPE.LOGGING, PE_KEYS.HOME_CATEGORY_CAROUSEL_ORDER_LOGGING, order.toString(), appContext, env);
  return array.toSorted((a, b) => order.indexOf(a.carouselId) - order.indexOf(b.carouselId));
};

/**
 * Helper function to move a carousel to a specific position in the sort order.
 */
const moveCarousel = (sortingOrderList: number[], carouselIdToMove: number, indexToMoveTo: number) => {
  const fromIndex = sortingOrderList.indexOf(carouselIdToMove);
  if (fromIndex === -1 || indexToMoveTo > sortingOrderList.length - 1) {
    return;
  }
  sortingOrderList.splice(fromIndex, 1);
  sortingOrderList.splice(indexToMoveTo, 0, carouselIdToMove);
};

/**
 * Custom hook to handle carousel sorting logic.
 * Applies backend sort order, mobile positioning, conditional promotion,
 * and dynamic carousel insertions.
 *
 * @param params - Sorting configuration parameters
 * @returns Sorted array of carousels with dynamic insertions
 */
export const useCarouselSorting = (params: UseCarouselSortingParams): CarouselToRender[] => {
  const appContext = useAppContext();
  const { env } = appContext;
  const storePageEvent = useStorePageEvent();
  const {
    carousels,
    sortOrder,
    isMobile,
    moveUpRecentlyViewedCarousel,
    isStubhub = false,
    hasSpotifyData = false,
    carouselSize,
    showRecentlyViewedAboveHeroBanner,
    showContinueBrowsingBanner = false,
    continueBrowsingBanner,
    showContinueWhereYouLeftOff = false,
    continueWhereYouLeftOffCarousel,
  } = params;

  return useMemo(() => {
    // Apply sorting rules to the base sort order
    const sortingOrderFiltered = [...sortOrder];

    moveCarousel(sortingOrderFiltered, HOME_CAROUSEL_IDS.recommendedForYou, 1);

    // Rule 1: On mobile, move recently viewed carousel to position 16
    if (isMobile) {
      moveCarousel(sortingOrderFiltered, HOME_CAROUSEL_IDS.recentlyViewed, 16);
    }

    // Rule 2: Conditionally promote recently viewed carousel to position 0
    const recentlyViewedIndex = sortingOrderFiltered.indexOf(HOME_CAROUSEL_IDS.recentlyViewed);
    if (recentlyViewedIndex !== -1 && moveUpRecentlyViewedCarousel) {
      // Log page event to track % of users in each variant
      storePageEvent(PE_TYPE.LOGGING, PE_KEYS.HOME_RECENTS_CAROUSEL_MOVED_UP, "");
      // Move to first position
      moveCarousel(sortingOrderFiltered, HOME_CAROUSEL_IDS.recentlyViewed, 0);
    }

    // Rule 3: Position email opt-in banner before comedy carousel
    const comedyIndex = sortingOrderFiltered.indexOf(HOME_CAROUSEL_IDS.comedy);
    if (comedyIndex !== -1) {
      moveCarousel(sortingOrderFiltered, HOME_CAROUSEL_IDS.emailOptInBanner, comedyIndex);
    }

    // hero should be at start by default
    moveCarousel(sortingOrderFiltered, HOME_CAROUSEL_IDS.heroBanner, 0);
    moveCarousel(sortingOrderFiltered, HOME_CAROUSEL_IDS.filters, 1);
    moveCarousel(
      sortingOrderFiltered,
      hasSpotifyData ? HOME_CAROUSEL_IDS.spotifyYourMusicCarousel : HOME_CAROUSEL_IDS.spotifyConnectBanner,
      2,
    );

    if (sortingOrderFiltered.includes(HOME_CAROUSEL_IDS.worldCup)) {
      moveCarousel(sortingOrderFiltered, HOME_CAROUSEL_IDS.worldCup, 2);
    }

    if (showRecentlyViewedAboveHeroBanner) {
      moveCarousel(sortingOrderFiltered, HOME_CAROUSEL_IDS.recentlyViewed, 0);
    }

    // Keep the favorite-team carousel directly below Recently Viewed, wherever the rules above put it.
    if (
      sortingOrderFiltered.includes(HOME_CAROUSEL_IDS.recentlyViewed) &&
      sortingOrderFiltered.includes(HOME_CAROUSEL_IDS.favoriteTeam)
    ) {
      sortingOrderFiltered.splice(sortingOrderFiltered.indexOf(HOME_CAROUSEL_IDS.favoriteTeam), 1);
      sortingOrderFiltered.splice(
        sortingOrderFiltered.indexOf(HOME_CAROUSEL_IDS.recentlyViewed) + 1,
        0,
        HOME_CAROUSEL_IDS.favoriteTeam,
      );
    }

    // Place the "More from Genres You Like" collection directly above the Concerts carousel
    // (exp 102911 marquee + its standard-carousel relaunch experiment — same slot depth keeps the
    // two runs comparable). The backend sort order (0..49) would otherwise sort id 29 to the bottom.
    // Only affects variant users — the slot is filtered out for everyone else, so their order is unchanged.
    const concertsIndex = sortingOrderFiltered.indexOf(HOME_CAROUSEL_IDS.concerts);
    if (concertsIndex !== -1) {
      moveCarousel(sortingOrderFiltered, HOME_CAROUSEL_IDS.mbMusicYouLike, concertsIndex);
    }

    // Place "Because You Browsed" directly above Recommended for You (exp 103386).
    const rfyIndex = sortingOrderFiltered.indexOf(HOME_CAROUSEL_IDS.recommendedForYou);
    if (rfyIndex !== -1) {
      moveCarousel(sortingOrderFiltered, HOME_CAROUSEL_IDS.becauseYouBrowsed, rfyIndex);
    }

    // Place "Coming Up For You" directly after Trending Events (DMS-225 / exp 103527).
    // ID 32 would otherwise sort near the bottom of the backend's Enumerable.Range(0, 50) order.
    const trendingEventsIndex = sortingOrderFiltered.indexOf(HOME_CAROUSEL_IDS.trendingEvents);
    if (trendingEventsIndex !== -1) {
      moveCarousel(sortingOrderFiltered, HOME_CAROUSEL_IDS.comingUpForYou, trendingEventsIndex + 1);
    }

    // Apply the sorting order to carousels
    const reordered = reorder(carousels, sortingOrderFiltered, appContext, env);

    // Rule 5: Insert FanProtect message at position 16 (becomes 17 after Spotify insertion at 0)
    const withFanProtect = reordered.toSpliced(17, 0, {
      carouselId: HOME_CAROUSEL_IDS.fanProtectMessage,
      kind: SEARCH_DISCOVERY_ENTITY_TYPES.CATEGORY,
      shouldRender: isStubhub && isMobile,
      render: () => <FanProtectMessage isMobile />,
    });

    // CWYLO (DMS-683, exp 104507) renders directly above Recently Viewed. Data is server-gated to
    // eligible experiment arms, and the slot is only added when the collection populated, so it
    // never shifts entityPosition for users who do not see it.
    const withCwylo =
      showContinueWhereYouLeftOff && continueWhereYouLeftOffCarousel
        ? withFanProtect.toSpliced(
            Math.max(
              withFanProtect.findIndex((carousel) => carousel.carouselId === HOME_CAROUSEL_IDS.recentlyViewed),
              0,
            ),
            0,
            {
              carouselId: HOME_CAROUSEL_IDS.continueWhereYouLeftOff,
              kind: SEARCH_DISCOVERY_ENTITY_TYPES.EVENT,
              shouldRender: true,
              render: () => (
                <CwyloCarouselOnHome
                  carousel={continueWhereYouLeftOffCarousel}
                  carouselSize={carouselSize ?? undefined}
                />
              ),
            },
          )
        : withFanProtect;

    // Exp 104039 (DMS-557): the "Continue looking" card renders directly above the Recently Viewed
    // slot. Data is server-gated to StubHub mobile-web banner variants, so `showContinueBrowsingBanner`
    // is the only client check. Spliced just before RV so it survives v2 (where RV output is empty).
    if (!showContinueBrowsingBanner || !continueBrowsingBanner) {
      return withCwylo;
    }

    const recentlyViewedPosition = withCwylo.findIndex(
      (carousel) => carousel.carouselId === HOME_CAROUSEL_IDS.recentlyViewed,
    );
    return withCwylo.toSpliced(recentlyViewedPosition === -1 ? 0 : recentlyViewedPosition, 0, {
      carouselId: HOME_CAROUSEL_IDS.continueBrowsingBanner,
      kind: SEARCH_DISCOVERY_ENTITY_TYPES.CATEGORY,
      shouldRender: true,
      render: () => <ContinueBrowsingBanner banner={continueBrowsingBanner} />,
    });
  }, [
    appContext,
    env,
    carousels,
    sortOrder,
    isMobile,
    moveUpRecentlyViewedCarousel,
    isStubhub,
    hasSpotifyData,
    showRecentlyViewedAboveHeroBanner,
    showContinueBrowsingBanner,
    continueBrowsingBanner,
    showContinueWhereYouLeftOff,
    continueWhereYouLeftOffCarousel,
    carouselSize,
    storePageEvent,
  ]);
};
